import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, MapPin, Globe, Mail, Phone, ClipboardCheck, TrendingUp, ShieldAlert, Box, KeyRound, Copy, Check, LinkIcon, Trash2 } from "lucide-react";
import { api } from "@/lib/api-client";
import { toRiskScore } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { StatCard } from "@/components/common/StatCard";
import { DataTable } from "@/components/common/DataTable";
import type { ColumnDef } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RiskScoreCircle } from "@/components/common/RiskScoreCircle";
import { PageSkeleton } from "@/components/common/LoadingSkeleton";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/Dialog";
import { toast } from "@/components/ui/Toast";
import { SelectRoot, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/Select";
import { useTranslation } from "@/hooks/useTranslation";
import type { VendorAssessmentStatus, VendorStatus, RiskLevel } from "@/types";

// ─────────────────────────────────────────────
// VendorProfile
//
// Screen — /vendors/:id
// KPI cards, assessment history, vendor info sidebar
// Design: §8.5 Vendor Profile
// ─────────────────────────────────────────────

interface VendorContact {
  name: string;
  email: string;
  phone?: string;
  role?: string;
}

interface VendorDetail {
  id: string;
  name: string;
  status: VendorStatus;
  industry?: string;
  // API/schema field is `location` (not `country`).
  location?: string;
  website?: string;
  description?: string;
  contacts?: VendorContact[];
  riskScore?: number;
  riskLevel?: RiskLevel;
  complianceRate?: number;
  totalAssessments?: number;
  // GET /vendors/:id includes _count.assessments (the real total).
  _count?: { assessments: number };
  createdAt: string;
}

interface VendorAssessmentHistoryRow {
  id: string;
  assessmentId: string;
  schemaName: string;
  status: VendorAssessmentStatus;
  sentAt: string | null;
  completedAt: string | null;
  dueDate: string | null;
  overallScore?: number | null;
  hasAccessToken?: boolean;
}

interface VendorDetailResponse {
  data: VendorDetail;
}

interface VendorAssessmentsResponse {
  data: VendorAssessmentHistoryRow[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
}

type EntityVendorRole = "DEVELOPMENT" | "HOSTING" | "ASSESSED";

interface VendorEntityRow {
  id: string;
  entityId: string;
  vendorId: string;
  role: EntityVendorRole;
  createdAt: string;
  entity: {
    id: string;
    name: string;
    type: string;
    status: string;
    applicationType: string | null;
    department: string | null;
  } | null;
}

interface VendorEntitiesResponse {
  data: VendorEntityRow[];
}

// Label + badge color per vendor-link role (all three enum values).
function buildEntityVendorRoleLabels(t: (key: string) => string): Record<EntityVendorRole, string> {
  return {
    DEVELOPMENT: t("vendor.roles.DEVELOPMENT"),
    HOSTING: t("vendor.roles.HOSTING"),
    ASSESSED: t("vendor.roles.ASSESSED"),
  };
}

const ENTITY_VENDOR_ROLE_CLASSES: Record<EntityVendorRole, string> = {
  DEVELOPMENT: "bg-accent-bg text-accent-text",
  HOSTING: "bg-k-purple-bg text-k-purple",
  ASSESSED: "bg-success-bg text-success",
};

// ─────────────────────────────────────────────
// GenerateAccessModal
// Shows the generated token + PIN to the admin one time.
// ─────────────────────────────────────────────

interface GeneratedAccess {
  accessToken: string;
  pin: string;
  vendorPortalUrl: string;
}

function GenerateAccessModal({
  open,
  onClose,
  assessmentId,
}: {
  open: boolean;
  onClose: () => void;
  assessmentId: string | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<GeneratedAccess | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedPin, setCopiedPin] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ data: GeneratedAccess }>(
        `/vendor-assessments/${assessmentId}/generate-access`
      ),
    onSuccess: (res) => {
      setResult(res.data);
      queryClient.invalidateQueries({ queryKey: ["vendor-assessments"] });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("vendor.generateAccessDialog.failedToast"));
    },
  });

  // Trigger generation when the modal opens (not during render — a render-time
  // mutate() double-fires under StrictMode and leaves the dialog stuck empty
  // after a failure). Runs once per open transition; retry is manual.
  useEffect(() => {
    if (open && !result && !mutation.isPending) {
      mutation.mutate();
    }
    // Intentionally keyed only on `open`; retry is via the error-state button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function copyToClipboard(text: string, type: "token" | "pin") {
    navigator.clipboard.writeText(text).then(() => {
      if (type === "token") {
        setCopiedToken(true);
        setTimeout(() => setCopiedToken(false), 2000);
      } else {
        setCopiedPin(true);
        setTimeout(() => setCopiedPin(false), 2000);
      }
    });
  }

  function handleClose() {
    setResult(null);
    setCopiedToken(false);
    setCopiedPin(false);
    // Clear error/pending state so reopening starts a fresh generation.
    mutation.reset();
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("vendor.generateAccessDialog.title")}</DialogTitle>
        </DialogHeader>

        {mutation.isPending && (
          <div className="py-8 text-center text-body text-text-muted">
            {t("vendor.generateAccessDialog.generating")}
          </div>
        )}

        {mutation.isError && !result && !mutation.isPending && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-body text-danger">
              {mutation.error instanceof Error
                ? mutation.error.message
                : t("vendor.generateAccessDialog.failedMessage")}
            </p>
            <Button variant="outline" onClick={() => mutation.mutate()}>
              {t("common.tryAgain")}
            </Button>
          </div>
        )}

        {result && (
          <div className="flex flex-col gap-4 py-2">
            <p className="text-body text-text-secondary">
              {t("vendor.generateAccessDialog.shareInstructions")}
            </p>

            <div className="rounded-card border border-border-default bg-hover p-4">
              <div className="mb-3">
                <p className="mb-1 text-caption font-medium text-text-muted">{t("vendorPortal.accessToken")}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-hover px-3 py-2 font-mono text-[13px] text-text-primary">
                    {result.accessToken}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(result.accessToken, "token")}
                    className="flex h-8 w-8 items-center justify-center rounded-control text-text-muted hover:text-text-primary"
                  >
                    {copiedToken ? (
                      <Check className="h-4 w-4 text-success" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <p className="mb-1 text-caption font-medium text-text-muted">{t("vendor.pin")}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-hover px-3 py-2 font-mono text-[20px] font-semibold tracking-[0.3em] text-text-primary">
                    {result.pin}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(result.pin, "pin")}
                    className="flex h-8 w-8 items-center justify-center rounded-control text-text-muted hover:text-text-primary"
                  >
                    {copiedPin ? (
                      <Check className="h-4 w-4 text-success" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-card border border-border-default bg-warning-bg px-4 py-3">
              <p className="text-caption text-warning">
                {t("vendor.generateAccessDialog.pinWarning")}
              </p>
            </div>

            <p className="text-caption text-text-muted">
              {t("vendor.generateAccessDialog.portalUrlLabel")}{" "}
              <a href="/vendor-portal/login" target="_blank" className="text-accent hover:underline">
                {window.location.origin}/vendor-portal/login
              </a>
            </p>
          </div>
        )}

        <DialogFooter>
          <Button onClick={handleClose}>
            {t("vendor.generateAccessDialog.confirmSaved")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VendorProfile() {
  const { t, language } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [generateAccessOpen, setGenerateAccessOpen] = useState(false);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string | null>(null);
  const [linkAppOpen, setLinkAppOpen] = useState(false);
  const [linkEntityId, setLinkEntityId] = useState("");
  const [linkRole, setLinkRole] = useState<"DEVELOPMENT" | "HOSTING" | "ASSESSED">("ASSESSED");
  const [startAssessmentOpen, setStartAssessmentOpen] = useState(false);
  const [saEntityId, setSaEntityId] = useState("");
  const [saSchemaId, setSaSchemaId] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [startAssessmentResult, setStartAssessmentResult] = useState<GeneratedAccess | null>(null);

  const { data: vendorRes, isLoading } = useQuery<VendorDetailResponse>({
    queryKey: ["vendor", id],
    queryFn: () => api.get(`/vendors/${id}`),
    enabled: Boolean(id),
  });

  const { data: assessmentsRes, isLoading: assessmentsLoading } = useQuery<VendorAssessmentsResponse>({
    queryKey: ["vendor-assessments", id],
    queryFn: () => api.get(`/vendor-assessments?vendorId=${id}&pageSize=20`),
    enabled: Boolean(id),
  });

  const { data: entitiesRes, isLoading: entitiesLoading } = useQuery<VendorEntitiesResponse>({
    queryKey: ["vendor-entities", id],
    queryFn: () => api.get(`/vendors/${id}/entities`),
    enabled: Boolean(id),
  });

  // All entities for the link / start-assessment dialogs
  const { data: allEntitiesRes, isLoading: allEntitiesLoading } = useQuery<{ data: Array<{ id: string; name: string }> }>({
    queryKey: ["entities-for-link"],
    queryFn: () => api.get("/entities?pageSize=100"),
    enabled: linkAppOpen || startAssessmentOpen,
  });

  // Schema versions for start assessment
  const { data: schemasRes } = useQuery<{ data: Array<{ id: string; name: string; versions: Array<{ id: string; version: number; status: string }> }> }>({
    queryKey: ["schemas-for-assessment"],
    queryFn: () => api.get("/schemas?pageSize=50"),
    enabled: startAssessmentOpen,
  });

  const linkMutation = useMutation({
    mutationFn: () =>
      api.post(`/entities/${linkEntityId}/vendors`, { vendorId: id, role: linkRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-entities", id] });
      toast.success(t("vendor.applicationLinked"));
      setLinkAppOpen(false);
      setLinkEntityId("");
      setLinkRole("ASSESSED");
    },
    onError: (err: Error) => toast.error(err.message ?? t("vendor.failedToLinkApplication")),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/vendors/${id}`),
    onSuccess: () => {
      toast.success(t("vendor.vendorDeleted"));
      navigate("/vendors");
    },
    onError: (err: Error) => toast.error(err.message ?? t("vendor.failedToDeleteVendor")),
  });

  // Start Assessment: create vendor assessment then generate access in one flow
  const schemasData = schemasRes?.data ?? [];
  const startAssessmentMutation = useMutation({
    mutationFn: async () => {
      const schema = schemasData.find((s) => s.id === saSchemaId);
      const publishedVersion = schema?.versions?.find((v) => v.status === "PUBLISHED");
      if (!publishedVersion) throw new Error(t("vendor.noPublishedVersion"));

      const vaRes = await api.post<{ data: { id: string; assessmentId: string } }>(`/vendors/${id}/assessments`, {
        schemaVersionId: publishedVersion.id,
        entityId: saEntityId,
      });
      const vaId = vaRes.data?.id;
      if (!vaId) throw new Error(t("vendor.failedToCreateVendorAssessment"));

      const accessRes = await api.post<{ data: GeneratedAccess }>(`/vendor-assessments/${vaId}/generate-access`);
      return accessRes.data;
    },
    onSuccess: (access) => {
      queryClient.invalidateQueries({ queryKey: ["vendor-assessments", id] });
      setStartAssessmentOpen(false);
      setSaEntityId("");
      setSaSchemaId("");
      setStartAssessmentResult(access);
    },
    onError: (err: Error) => toast.error(err.message ?? t("vendor.failedToStartAssessment")),
  });

  // ── Early returns (after all hooks) ──────────────────────
  if (isLoading) return <PageSkeleton />;

  const vendor = vendorRes?.data;
  if (!vendor) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-text-muted">
        {t("vendor.notFound")}
      </div>
    );
  }

  const assessments = assessmentsRes?.data ?? [];
  const linkedEntities = entitiesRes?.data ?? [];
  const allEntities = allEntitiesRes?.data ?? [];
  const allSchemas = schemasData;

  function handleGenerateAccess(assessmentId: string) {
    setSelectedAssessmentId(assessmentId);
    setGenerateAccessOpen(true);
  }

  const entityVendorRoleLabels = buildEntityVendorRoleLabels(t);

  const historyColumns: ColumnDef<VendorAssessmentHistoryRow>[] = [
    {
      key: "schema",
      header: t("assessment.schema"),
      cell: (row) => <span className="font-medium text-text-primary">{row.schemaName}</span>,
    },
    {
      key: "status",
      header: t("entity.status"),
      cell: (row) => <StatusBadge type="vendorAssessment" status={row.status} />,
    },
    {
      key: "score",
      header: t("assessment.score"),
      cell: (row) =>
        row.overallScore !== null && row.overallScore !== undefined ? (
          <span className="font-mono text-accent-light">{toRiskScore(row.overallScore)}</span>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "sentAt",
      header: t("vendor.sent"),
      cell: (row) =>
        row.sentAt ? (
          <span className="text-text-secondary">{formatDate(row.sentAt, language)}</span>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "completedAt",
      header: t("status.completed"),
      cell: (row) =>
        row.completedAt ? (
          <span className="text-text-secondary">{formatDate(row.completedAt, language)}</span>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "dueDate",
      header: t("vendor.dueDate"),
      cell: (row) =>
        row.dueDate ? (
          <span className="text-text-secondary">{formatDate(row.dueDate, language)}</span>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "access",
      header: t("vendor.portalAccess"),
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleGenerateAccess(row.id);
          }}
        >
          <KeyRound className="mr-1.5 h-3.5 w-3.5" />
          {row.hasAccessToken ? t("vendor.regenerate") : t("vendor.generateAccess")}
        </Button>
      ),
    },
  ];

  return (
    <>
      {/* Back nav */}
      <button
        onClick={() => navigate("/vendors")}
        className="flex items-center gap-2 text-body text-text-muted hover:text-text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("vendor.backToVendors")}
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-card bg-hover">
            <Building2 className="h-6 w-6 text-text-muted" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[24px] font-semibold text-text-primary">{vendor.name}</h1>
              <StatusBadge type="vendor" status={vendor.status} />
            </div>
            {vendor.description && (
              <p className="mt-1 text-body text-text-muted">{vendor.description}</p>
            )}
          </div>
        </div>
        <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} leadingIcon={<Trash2 className="h-3.5 w-3.5" />}>
          {t("common.delete")}
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          icon={ClipboardCheck}
          label={t("vendor.totalAssessments")}
          value={vendor._count?.assessments ?? vendor.totalAssessments ?? assessments.length}
        />
        <StatCard
          icon={TrendingUp}
          label={t("vendor.complianceRate")}
          value={vendor.complianceRate !== null && vendor.complianceRate !== undefined ? `${vendor.complianceRate}%` : "-"}
        />
        <div className="rounded-card border border-border-default bg-card p-5">
          <div className="flex items-center gap-inner mb-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-control bg-hover">
              <ShieldAlert className="h-4 w-4 text-text-muted" />
            </div>
            <span className="text-caption text-text-muted">{t("risk.score")}</span>
          </div>
          {vendor.riskScore !== null && vendor.riskScore !== undefined ? (
            <RiskScoreCircle score={vendor.riskScore} riskLevel={vendor.riskLevel ?? "LOW"} />
          ) : (
            <div className="flex items-center justify-center py-6 text-caption text-text-muted">
              {t("vendor.noAssessmentsCompleted")}
            </div>
          )}
        </div>
      </div>

      {/* Applications section */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[16px] font-semibold text-text-primary">{t("vendor.applications")}</h2>
          <Button size="sm" variant="outline" onClick={() => setLinkAppOpen(true)} leadingIcon={<LinkIcon className="h-3.5 w-3.5" />}>
            {t("vendor.linkApplication")}
          </Button>
        </div>
        {entitiesLoading ? (
          <div className="text-body text-text-muted py-4">{t("common.loading")}</div>
        ) : linkedEntities.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-card border border-border-subtle bg-card py-8 text-center">
            <Box className="mb-2 h-8 w-8 text-text-muted" />
            <p className="text-body font-medium text-text-secondary">{t("vendor.noLinkedApplications")}</p>
            <p className="mt-1 text-caption text-text-muted">
              {t("vendor.noLinkedApplicationsDesc")}
            </p>
          </div>
        ) : (
          <div className="rounded-card border border-border-default bg-card overflow-hidden">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="px-4 py-3 text-left text-caption text-text-muted font-medium">{t("entity.types.APPLICATION")}</th>
                  <th className="px-4 py-3 text-left text-caption text-text-muted font-medium">{t("users.role")}</th>
                  <th className="px-4 py-3 text-left text-caption text-text-muted font-medium">{t("entity.department")}</th>
                  <th className="px-4 py-3 text-left text-caption text-text-muted font-medium">{t("entity.status")}</th>
                </tr>
              </thead>
              <tbody>
                {linkedEntities.map((ev) => (
                  <tr key={ev.id} className="border-b border-border-subtle last:border-0 hover:bg-hover">
                    <td className="px-4 py-3">
                      {ev.entity ? (
                        <Link
                          to={`/apps/${ev.entityId}`}
                          className="font-medium text-accent hover:underline"
                        >
                          {ev.entity.name}
                        </Link>
                      ) : (
                        <span className="text-text-muted">{ev.entityId}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-badge px-2 py-0.5 text-[12px] font-semibold leading-none ${
                          ENTITY_VENDOR_ROLE_CLASSES[ev.role] ?? "bg-hover text-text-muted"
                        }`}
                      >
                        {entityVendorRoleLabels[ev.role] ?? ev.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {ev.entity?.department ?? "-"}
                    </td>
                    <td className="px-4 py-3">
                      {ev.entity ? (
                        <StatusBadge type="entity" status={ev.entity.status as never} />
                      ) : (
                        <span className="text-text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Main content: history + sidebar */}
      <div className="flex gap-6">
        {/* Assessment history table */}
        <div className="flex-1 min-w-0">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[16px] font-semibold text-text-primary">{t("vendor.assessmentHistory")}</h2>
            <Button size="sm" onClick={() => setStartAssessmentOpen(true)} leadingIcon={<ClipboardCheck className="h-3.5 w-3.5" />}>
              {t("vendor.startAssessment")}
            </Button>
          </div>
          <DataTable
            columns={historyColumns}
            data={assessments}
            isLoading={assessmentsLoading}
            // Vendor rows open the vendor review page (scores/approve for
            // UNDER_REVIEW, read-only for COMPLETED) — not the internal
            // assessment fill page for the vendor's entity.
            onRowClick={(row) => navigate(`/vendors/${id}/assessments/${row.id}`)}
            emptyState={{
              icon: ClipboardCheck,
              title: t("reports.noAssessmentsYet"),
              description: t("vendor.noAssessmentsSentDesc"),
            }}
          />
        </div>

        {/* Vendor info sidebar */}
        <div className="w-72 flex-shrink-0">
          <div className="rounded-card border border-border-default bg-card p-5">
            <h3 className="mb-4 text-[14px] font-semibold text-text-primary">{t("vendor.vendorInfo")}</h3>
            <dl className="flex flex-col gap-3">
              {vendor.industry && (
                <div>
                  <dt className="text-caption text-text-muted">{t("vendor.industry")}</dt>
                  <dd className="mt-0.5 text-body text-text-secondary">{vendor.industry}</dd>
                </div>
              )}
              {vendor.location && (
                <div>
                  <dt className="flex items-center gap-1 text-caption text-text-muted">
                    <MapPin className="h-3 w-3" /> {t("vendor.location")}
                  </dt>
                  <dd className="mt-0.5 text-body text-text-secondary">{vendor.location}</dd>
                </div>
              )}
              {vendor.website && (
                <div>
                  <dt className="flex items-center gap-1 text-caption text-text-muted">
                    <Globe className="h-3 w-3" /> {t("vendor.website")}
                  </dt>
                  <dd className="mt-0.5">
                    <a
                      href={vendor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body text-accent hover:underline"
                    >
                      {vendor.website}
                    </a>
                  </dd>
                </div>
              )}
              <div className="my-1 h-px bg-border-subtle" />
              <div>
                <dt className="text-caption text-text-muted mb-2">{t("vendor.contacts")}</dt>
                {vendor.contacts && vendor.contacts.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {vendor.contacts.map((c, i) => (
                      <div key={i} className="flex flex-col gap-0.5">
                        <span className="text-[14px] font-medium text-text-primary">{c.name}</span>
                        {c.role && <span className="text-caption text-text-muted">{c.role}</span>}
                        <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-caption text-accent">
                          <Mail className="h-3 w-3" />
                          {c.email}
                        </a>
                        {c.phone && (
                          <span className="flex items-center gap-1 text-caption text-text-muted">
                            <Phone className="h-3 w-3" />
                            {c.phone}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-caption text-text-muted">{t("vendor.noContactsOnRecord")}</span>
                )}
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("vendor.deleteDialog.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-[14px] text-text-secondary">
            {t("vendor.deleteDialog.descPre")} <strong>{vendor.name}</strong>{t("vendor.deleteDialog.descPost")}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate()} isLoading={deleteMutation.isPending}>
              {t("vendor.deleteDialog.title")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Access Modal */}
      <GenerateAccessModal
        open={generateAccessOpen}
        onClose={() => {
          setGenerateAccessOpen(false);
          setSelectedAssessmentId(null);
        }}
        assessmentId={selectedAssessmentId}
      />

      {/* Link Application Dialog */}
      <Dialog open={linkAppOpen} onOpenChange={(v) => { if (!v) { setLinkAppOpen(false); setLinkEntityId(""); setLinkRole("ASSESSED"); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("vendor.linkDialog.title")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">{t("entity.types.APPLICATION")}</label>
              <SelectRoot value={linkEntityId} onValueChange={setLinkEntityId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("vendor.sendDialog.selectEntity")} />
                </SelectTrigger>
                <SelectContent>
                  {allEntities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">{t("users.role")}</label>
              <SelectRoot value={linkRole} onValueChange={(v) => setLinkRole(v as "DEVELOPMENT" | "HOSTING" | "ASSESSED")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ASSESSED">{entityVendorRoleLabels.ASSESSED}</SelectItem>
                  <SelectItem value="DEVELOPMENT">{entityVendorRoleLabels.DEVELOPMENT}</SelectItem>
                  <SelectItem value="HOSTING">{entityVendorRoleLabels.HOSTING}</SelectItem>
                </SelectContent>
              </SelectRoot>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkAppOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => linkMutation.mutate()} isLoading={linkMutation.isPending} disabled={!linkEntityId}>
              {t("vendor.linkDialog.confirmButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Assessment Dialog */}
      <Dialog open={startAssessmentOpen} onOpenChange={(v) => { if (!v) { setStartAssessmentOpen(false); setSaEntityId(""); setSaSchemaId(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("vendor.startAssessmentDialog.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-[14px] text-text-secondary">
            {t("vendor.startAssessmentDialog.description")}
          </p>
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">{t("entity.types.APPLICATION")}</label>
              <SelectRoot value={saEntityId} onValueChange={setSaEntityId}>
                <SelectTrigger>
                  <SelectValue placeholder={allEntitiesLoading ? t("common.loading") : t("vendor.sendDialog.selectEntity")} />
                </SelectTrigger>
                <SelectContent>
                  {allEntities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">{t("assessment.schema")}</label>
              <SelectRoot value={saSchemaId} onValueChange={setSaSchemaId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("vendor.sendDialog.selectSchema")} />
                </SelectTrigger>
                <SelectContent>
                  {allSchemas.filter((s) => s.versions?.some((v) => v.status === "PUBLISHED")).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStartAssessmentOpen(false)}>{t("common.cancel")}</Button>
            <Button
              onClick={() => startAssessmentMutation.mutate()}
              isLoading={startAssessmentMutation.isPending}
              disabled={!saEntityId || !saSchemaId}
            >
              {t("vendor.startAssessmentDialog.confirmButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show access credentials after Start Assessment */}
      {startAssessmentResult && (
        <Dialog open={!!startAssessmentResult} onOpenChange={() => setStartAssessmentResult(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("vendor.accessGeneratedDialog.title")}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-2">
              <p className="text-body text-text-secondary">
                {t("vendor.generateAccessDialog.shareInstructions")}
              </p>
              <div className="rounded-card border border-border-default bg-hover p-4">
                <div className="mb-3">
                  <p className="mb-1 text-caption font-medium text-text-muted">{t("vendor.portalUrl")}</p>
                  <a
                    href={startAssessmentResult.vendorPortalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded bg-hover px-3 py-2 font-mono text-[13px] text-accent hover:underline break-all"
                  >
                    {startAssessmentResult.vendorPortalUrl}
                  </a>
                </div>
                <div className="mb-3">
                  <p className="mb-1 text-caption font-medium text-text-muted">{t("vendorPortal.accessToken")}</p>
                  <code className="block rounded bg-hover px-3 py-2 font-mono text-[13px] text-text-primary break-all">
                    {startAssessmentResult.accessToken}
                  </code>
                </div>
                <div>
                  <p className="mb-1 text-caption font-medium text-text-muted">{t("vendor.pin")}</p>
                  <code className="block rounded bg-hover px-3 py-2 font-mono text-[16px] font-bold text-text-primary tracking-widest">
                    {startAssessmentResult.pin}
                  </code>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setStartAssessmentResult(null)}>{t("common.done")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
